import apiServiceAdmin from "@services/apiServiceAdmin";
import ReimbursementRequestService from "@/services/clubs/reimbursementRequestService";

export async function listing(params) {
  const response = await apiServiceAdmin.post('/reimbursement_requests/pending', params);
  
  if (response.data.success && response.data.data) {
    const transformedRequests = (response.data.data.pending_applications || []).map(app => 
      transformListingData(app)
    );
    
    return {
      ...response.data,
      data: {
        requests: transformedRequests,
        page_meta: response.data.data.page_meta
      }
    };
  }
  
  return response.data;
}

export async function approvedByMeListing(params) {
  const response = await apiServiceAdmin.post('/reimbursement_requests/approved_by_me', params);

  if (response.data.success && response.data.data) {
    const transformedRequests = (response.data.data.approved_applications || []).map(app =>
      transformListingData(app)
    );

    return {
      ...response.data,
      data: {
        requests: transformedRequests,
        page_meta: response.data.data.page_meta
      }
    };
  }

  return response.data;
}

export async function show(id) {
  const response = await apiServiceAdmin.get(`/reimbursement_requests/${id}`);
  
  if (response.data.success && response.data.data) {
    const transformedData = ReimbursementRequestService.transformApiDataForDisplay(response.data.data);
    return {
      success: true,
      message: response.data.message || "",
      data: transformedData
    };
  }
  
  return response.data;
}

export async function refreshBalance(id) {
  const response = await apiServiceAdmin.post(`/reimbursement_requests/${id}/refresh_balance`);
  return response.data;
}

export async function approve(id) {
  const response = await apiServiceAdmin.patch(`/reimbursement_requests/${id}/approve`);
  return response.data;
}

export async function processPayment(id) {
  const response = await apiServiceAdmin.patch(`/reimbursement_requests/${id}/process_payment`);
  return response.data;
}

export async function markRequestsReadyForPickup(reimbursementRequestIds) {
  const response = await apiServiceAdmin.post('/reimbursement_requests/mark_ready_for_pickup', {
    reimbursement_request_ids: reimbursementRequestIds
  });
  return response.data;
}

export async function mailDeliverMultiple(reimbursementRequestIds, deliverBy) {
  const response = await apiServiceAdmin.post('/reimbursement_requests/mail_deliver_multiple', {
    reimbursement_request_ids: reimbursementRequestIds,
    deliver_by: deliverBy,
  });
  return response.data;
}

// New endpoint functions
export async function generateCoverSheet(batchId) {
  const response = await apiServiceAdmin.post(`/reimbursement_request_batches/${batchId}/generate_cover_sheet`);
  return response.data;
}

export async function generateBackup(batchId) {
  const response = await apiServiceAdmin.post(`/reimbursement_request_batches/${batchId}/generate_backup`);
  return response.data;
}

// Old function names (backward compatibility aliases)
export async function exportBatchSummary(batchId) {
  return generateCoverSheet(batchId);
}

export async function exportBackup(batchId) {
  return generateBackup(batchId);
}

export async function addAllToCurrentBatch() {
  const response = await apiServiceAdmin.put(`/reimbursement_request_batches/add_all_processing_requests_to_batch`);
  return response.data;
}

export async function importToBusinessCentral(batchId) {
  const response = await apiServiceAdmin.post(`/reimbursement_request_batches/${batchId}/import_to_business_central`);
  return response.data;
}

export async function getBatch(batchId) {
  const response = await apiServiceAdmin.get(`/reimbursement_request_batches/${batchId}`);
  return response.data;
}

export async function markBatchProcessed(batchId) {
  const response = await apiServiceAdmin.post(`/reimbursement_request_batches/${batchId}/mark_processed`);
  return response.data;
}

export async function getBatchDocuments(batchId, documentTypes = null) {
  const params = documentTypes ? { document_types: documentTypes } : {};
  const response = await apiServiceAdmin.get(`/reimbursement_request_batches/${batchId}/documents`, { params });
  return response.data;
}

export async function getBatchesListing(params = {}) {
  const response = await apiServiceAdmin.post('/reimbursement_request_batches/search', params);
  return response.data;
}

export async function getActiveBatch() {
  const response = await apiServiceAdmin.get('/reimbursement_request_batches/active_batch');
  return response.data;
}

/**
 * Free-text "jump to request" search for the Finance Operations command menu.
 * Matches one term across cheque id, invoice id, request/expense descriptions,
 * club name, submitter WatIAM id, and event form id. Results are scoped
 * server-side to requests the operator may open.
 *
 * @param {Object} options
 * @param {string} options.q - search term
 * @param {number} [options.limit=10] - max results
 * @returns {Promise<Object>} standardized API envelope: { success, data: { results, query } }
 */
export async function searchRequests({ q, limit = 10 } = {}) {
  const response = await apiServiceAdmin.get('/reimbursement_requests/search', {
    params: { q, limit },
  });
  return response.data;
}

export async function reject(id, comment) {
  const response = await apiServiceAdmin.patch(`/reimbursement_requests/${id}/reject`, {
    comment: comment
  });
  return response.data;
}

export async function sendback(id, params = {}) {
  const response = await apiServiceAdmin.patch(`/reimbursement_requests/${id}/send_back`, params);
  return response.data;
}

export async function sendToPreviousSeat(id, params = {}) {
  const response = await apiServiceAdmin.patch(`/reimbursement_requests/${id}/send_to_previous_seat`, params);
  return response.data;
}

export async function deliver(id, deliverByName = '') {
  const response = await apiServiceAdmin.patch(`/reimbursement_requests/${id}/deliver`, {
    deliver_by: deliverByName
  });
  return response.data;
}

export async function pickupReminder(id) {
  const response = await apiServiceAdmin.patch(`/reimbursement_requests/${id}/pickup_reminder`);
  return response.data;
}

export async function clearImportError(reimbursementRequestId, { resolutionType, resolutionNote } = {}) {
  const response = await apiServiceAdmin.patch(
    `/reimbursement_request_batches/reimbursement_requests/${reimbursementRequestId}/clear_import_error`,
    { resolution_type: resolutionType, resolution_note: resolutionNote }
  );
  return response.data;
}

export async function withdraw(id) {
  const reponse = await apiServiceAdmin.patch(`/reimbursement_requests/${id}/withdraw`);
  return reponse.data;
}

export async function uploadLineDocuments(lineId, signedIds) {
  const response = await apiServiceAdmin.post(
    `/reimbursement_request_lines/${lineId}/upload_line_documents`,
    { signed_ids: signedIds }
  );
  return response.data;
}

export async function getSetup() {
  const response = await apiServiceAdmin.get('/reimbursement_requests/setup');
  return response.data;
}

export async function getReimbursementRequest(id) {
  const response = await apiServiceAdmin.get(`/reimbursement_requests/${id}`); 
  return response.data;
}

export async function updateReimbursementRequest(id, data) {
  const response = await apiServiceAdmin.patch(`/reimbursement_requests/${id}`, data);
  return response.data;
}

export function transformListingData(data) {
  let reimbursementRequest = {
    id: data.id,
    slug: data.slug,
    club_id: data.club?.id,
    club_name: data.club?.name,
    club_acronym: data.club?.acronym,
    description: data.reimbursement_description,
    amount: data.total,
    submitted_by: data.submitted_by?.full_name || data.submitted_by?.name,
    submitted_by_email: data.submitted_by?.email,
    submitted_at: data.created_at,
    submitted_at_fmt: data.created_at_fmt,
    aasm_state: data.aasm_state,
    status: data.aasm_state,
    state: data.aasm_state,
    status_text: data.status_text,
    form_id: data.form_id,
    form_version: data.form_version,
    updated_at: data.updated_at,
    updated_at_fmt: data.updated_at_fmt,
    cheque_payable_to: data.cheque_payable_name,
    policies: data.policies || {},
  };

  if (data.hold_details) {
    reimbursementRequest.hold_details = data.hold_details;
  }

  if (data.batch_name) {
    reimbursementRequest.batch_name = data.batch_name;
  }

  return reimbursementRequest;
}

export default {
  listing,
  approvedByMeListing,
  show,
  approve,
  processPayment,
  markRequestsReadyForPickup,
  mailDeliverMultiple,
  generateCoverSheet,
  generateBackup,
  exportBatchSummary,  // Backward compatibility alias
  exportBackup,        // Backward compatibility alias
  addAllToCurrentBatch,
  importToBusinessCentral,
  getBatch,
  markBatchProcessed,
  getBatchDocuments,
  getBatchesListing,
  getActiveBatch,
  reject,
  sendback,
  sendToPreviousSeat,
  deliver,
  pickupReminder,
  clearImportError,
  withdraw,
  uploadLineDocuments,
  getSetup,
  getReimbursementRequest,
  updateReimbursementRequest,
  transformListingData
};